import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../http';
import SkillChecklistCard from './SkillChecklistCard';

export default function StationView({ station, testEvent, session, setSession, view, refreshSignal }) {
  const [stationState, setStationState] = useState('waiting');
  const [currentCompany, setCurrentCompany] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState([]);

  // Session-wide timer (seconds elapsed since testing step began)
  const [sessionTimer, setSessionTimer] = useState(0);
  const timerRef = useRef(null);

  // Station-wide results history (all sessions, all proctors)
  const [stationResults, setStationResults] = useState([]);

  // Minimize-all counter — incrementing triggers all SkillChecklistCards to collapse
  const [collapseAll, setCollapseAll] = useState(0);

  // Briefing / skill selection state
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const [selectedSetByGroup, setSelectedSetByGroup] = useState({}); // { group_id: set }  — company-mode groups
  const [candidatePathMap, setCandidatePathMap] = useState({});     // { group_id: { candidate_id: set } } — individual-mode groups
  // eslint-disable-next-line no-unused-vars
  const [skillCandidateMap, setSkillCandidateMap] = useState({});   // { skill_id: candidate_id[] | null } — used for legacy individual overrides
  const [assigningSkills, setAssigningSkills] = useState(false);

  // Track which session we've already auto-initialized briefing selections for.
  // Without this, every loadData() call (10s poll + websocket events) would
  // create a new `session` object reference and re-run the init effect,
  // resetting whatever the proctor had selected.
  const briefingInitializedRef = useRef(null);

  // Inline confirmation state (replaces window.confirm)
  const [confirmAction, setConfirmAction] = useState(null); // null | 'complete' | 'return'

  const navigate = useNavigate();

  // ─── Data Loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!station) return;
    try {
      const statusRes = await api.get('/api/proctor/station-status');

      if (statusRes.data.status === 'reassigned') {
        alert('You have been reassigned to a new station. Please verify the new station PIN.');
        navigate('/proctor/verify-station');
        return;
      }

      setStationState(statusRes.data.status);
      setCurrentCompany({
        id: statusRes.data.station?.current_company_id,
        name: statusRes.data.station?.current_company_name,
        status: statusRes.data.station?.current_company_status
      });

      const activeSessionId = statusRes.data.activeSessionId;

      const [resultsRes, ...sessionFetch] = await Promise.all([
        api.get('/api/proctor/station-results'),
        ...(activeSessionId
          ? [api.get(`/api/test-sessions/${activeSessionId}`), api.get('/api/proctor/workflow-steps')]
          : [])
      ]);

      setStationResults(Array.isArray(resultsRes.data) ? resultsRes.data : []);

      if (activeSessionId) {
        const [detailRes, stepsRes] = sessionFetch;
        setWorkflowSteps(Array.isArray(stepsRes.data) ? stepsRes.data : []);
        setSession(detailRes.data);
        setCandidates(detailRes.data.candidates || []);
        setSkills(detailRes.data.skill_assignments || []);
      } else {
        setSession(null);
        setCandidates([]);
        setSkills([]);
        setWorkflowSteps([]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      // 401s are handled globally by the http interceptor (auth_unauthorized event + modal).
      // Don't navigate here — that would dismiss any in-progress offline queue and bypass the modal.
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [station, setSession, navigate]);

  // Reload whenever the station context changes (loadData ref changes) or
  // ProctorApp signals a refresh via a WebSocket event or polling tick.
  useEffect(() => {
    loadData();
  }, [loadData, refreshSignal]);

  // ─── Session timer: starts when current_action === 'testing' ────────────────

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (session?.current_action === 'testing' && session?.action_timestamp) {
      const base = new Date(session.action_timestamp).getTime();
      setSessionTimer(Math.floor((Date.now() - base) / 1000));
      timerRef.current = setInterval(() => {
        setSessionTimer(Math.floor((Date.now() - base) / 1000));
      }, 1000);
    } else {
      setSessionTimer(0);
    }

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [session?.current_action, session?.action_timestamp]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const handleRefresh = () => { setRefreshing(true); loadData(); };

  const handleAdvanceStep = async (stepKey) => {
    if (!session) return;
    try {
      await api.post('/api/test-sessions/advance', { session_id: session.id, step_key: stepKey }, { queueable: true });
      loadData();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to advance step');
    }
  };

  const getNextProctorStep = () => {
    if (!session || workflowSteps.length === 0) return null;
    const currentAction = session.current_action || workflowSteps[0]?.key;
    const currentIdx = workflowSteps.findIndex((s) => s.key === currentAction);
    const currentStep = workflowSteps[currentIdx];
    if (currentStep?.is_testing_step) return null;
    for (let i = currentIdx + 1; i < workflowSteps.length; i++) {
      const s = workflowSteps[i];
      if (s.editable_by === 'proctor' || s.editable_by === 'both') return s;
    }
    return null;
  };

  // First proctor-editable step that comes AFTER the testing step.
  // Used when the proctor clicks "Testing Complete" — advances to debrief / return
  // instead of immediately releasing the company.
  const getFirstPostTestingStep = () => {
    if (!workflowSteps.length) return null;
    const testingIdx = workflowSteps.findIndex((s) => s.is_testing_step);
    if (testingIdx === -1) return null;
    for (let i = testingIdx + 1; i < workflowSteps.length; i++) {
      const s = workflowSteps[i];
      if (s.editable_by === 'proctor' || s.editable_by === 'both') return s;
    }
    return null;
  };

  // True when the current state is a step that comes after the testing step.
  // Used in the queue view to decide whether to show "Release Company".
  const isPostTestingState = (() => {
    if (!workflowSteps.length || !stationState) return false;
    const testingIdx = workflowSteps.findIndex((s) => s.is_testing_step);
    const currentIdx = workflowSteps.findIndex((s) => s.key === stationState);
    return testingIdx !== -1 && currentIdx > testingIdx;
  })();

  const handleCompleteSession = async () => {
    if (!session) return;
    setConfirmAction('complete');
  };

  const handleReturnToStaging = async () => {
    if (!session) return;
    setConfirmAction('return');
  };

  const handleConfirmAction = async () => {
    if (confirmAction === 'complete') {
      // If we're finishing a testing step and there are post-testing workflow steps,
      // advance to the next step (e.g. debrief) instead of releasing the company.
      const postTestStep = stationState === 'testing' ? getFirstPostTestingStep() : null;
      if (postTestStep) {
        try {
          await api.post('/api/test-sessions/advance', { session_id: session.id, step_key: postTestStep.key }, { queueable: true });
          loadData();
          navigate('/proctor/queue');
        } catch (err) { alert(err?.response?.data?.error || 'Failed to advance workflow'); }
      } else {
        // No post-testing steps (or already past testing) — release the company.
        try {
          await api.post('/api/test-sessions/complete', { session_id: session.id }, { queueable: true });
          loadData();
        } catch (err) { alert('Failed to complete session'); }
      }
    } else if (confirmAction === 'return') {
      try {
        await api.post('/api/test-sessions/return', { session_id: session.id }, { queueable: true });
        loadData();
      } catch (err) { alert('Failed to return to staging'); }
    }
    setConfirmAction(null);
  };

  const handleCancelAction = () => setConfirmAction(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ─── Briefing / skill selection helpers ──────────────────────────────────────

  // Build the initial selection from the session groups.
  // Auto-selects single-set groups and 'all' mode groups; builds skillCandidateMap.
  // Only runs once per session — guarded by briefingInitializedRef so that the
  // 10-second poll and websocket events don't reset the proctor's selections.
  useEffect(() => {
    if (stationState !== 'briefing' || !session?.groups) return;
    // Already initialized for this session — don't reset the proctor's choices.
    if (briefingInitializedRef.current === session.id) return;
    const preSelected = [];
    const preMap = {};
    const preSetsByGroup = {};

    for (const group of session.groups) {
      let autoSet = null;
      if (group.selection_mode === 'all') {
        // Legacy: all skills in every set are pre-selected
        for (const set of group.sets || []) {
          for (const sk of set.skills || []) {
            if (!preSelected.includes(sk.id)) preSelected.push(sk.id);
            preMap[sk.id] = sk.testing_mode === 'individual' ? candidates.map((c) => c.id) : null;
          }
        }
      } else if ((group.sets || []).length === 1) {
        // Only one path available — auto-select it
        autoSet = group.sets[0];
      }
      if (autoSet) {
        preSetsByGroup[group.id] = autoSet;
        for (const sk of autoSet.skills || []) {
          if (!preSelected.includes(sk.id)) preSelected.push(sk.id);
          preMap[sk.id] = sk.testing_mode === 'individual' ? candidates.map((c) => c.id) : null;
        }
      }
    }

    setSelectedSkillIds(preSelected);
    setSkillCandidateMap(preMap);
    setSelectedSetByGroup(preSetsByGroup);
    briefingInitializedRef.current = session.id;
  }, [stationState, session?.id, session?.groups, candidates]);

  const toggleSkill = (skillId) => {
    setSelectedSkillIds((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId]
    );
  };

  const selectSet = (set, group) => {
    const groupSkillIds = (group.sets || []).flatMap((s) => (s.skills || []).map((sk) => sk.id));
    const newSetSkillIds = (set.skills || []).map((sk) => sk.id);

    setSelectedSkillIds((prev) => [
      ...prev.filter((id) => !groupSkillIds.includes(id)),
      ...newSetSkillIds
    ]);

    setSelectedSetByGroup((prev) => ({ ...prev, [group.id]: set }));

    // Build candidate map for this set's skills
    const groupSkillIdSet = new Set(groupSkillIds);
    setSkillCandidateMap((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => !groupSkillIdSet.has(Number(id)))
      );
      for (const sk of set.skills || []) {
        next[sk.id] = sk.testing_mode === 'individual' ? candidates.map((c) => c.id) : null;
      }
      return next;
    });
  };

  // Per-candidate path selection — used for individual-mode groups.
  const selectSetForCandidate = (set, group, candidateId) => {
    setCandidatePathMap((prev) => ({
      ...prev,
      [group.id]: { ...(prev[group.id] || {}), [candidateId]: set }
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const toggleCandidateForSkill = (skillId, candidateId) => {
    setSkillCandidateMap((prev) => {
      const current = prev[skillId] || [];
      const updated = current.includes(candidateId)
        ? current.filter((id) => id !== candidateId)
        : [...current, candidateId];
      return { ...prev, [skillId]: updated };
    });
  };

  const handleBeginTesting = async () => {
    if (!session) return;
    const groups = session.groups || [];

    // Validate all group selections are complete
    for (const group of groups) {
      if (group.selection_mode === 'all') continue;
      if (group.assignment_mode === 'individual') {
        for (const candidate of candidates) {
          if (!candidatePathMap[group.id]?.[candidate.id]) {
            alert(`Please assign a path for ${candidate.first_name} ${candidate.last_name} in "${group.name || 'the group'}".`);
            return;
          }
        }
      } else {
        if ((group.sets || []).length > 1 && !selectedSetByGroup[group.id]) {
          alert(`Please select a path for "${group.name || 'the group'}".`);
          return;
        }
      }
    }

    const assignments = [];
    const addedTeamKeys = new Set(); // deduplicate team-skill entries

    for (const group of groups) {
      if (group.selection_mode === 'all') {
        // Legacy: all skills from every set, use skill-level testing_mode
        for (const set of group.sets || []) {
          for (const sk of set.skills || []) {
            if (sk.testing_mode === 'individual') {
              assignments.push({ skill_id: sk.id, candidate_ids: candidates.map((c) => c.id) });
            } else if (!addedTeamKeys.has(sk.id)) {
              assignments.push({ skill_id: sk.id, candidate_ids: null });
              addedTeamKeys.add(sk.id);
            }
          }
        }
      } else if (group.assignment_mode === 'individual') {
        // Per-candidate: each candidate may be on a different path
        for (const candidate of candidates) {
          const chosenSet = candidatePathMap[group.id]?.[candidate.id];
          if (!chosenSet) continue;
          for (const sk of chosenSet.skills || []) {
            if (sk.testing_mode === 'team') {
              // Skill-level team override: add once for the company
              const teamKey = `team-${sk.id}`;
              if (!addedTeamKeys.has(teamKey)) {
                assignments.push({ skill_id: sk.id, candidate_ids: null });
                addedTeamKeys.add(teamKey);
              }
            } else {
              assignments.push({ skill_id: sk.id, candidate_ids: [candidate.id] });
            }
          }
        }
      } else {
        // Company mode: one path for the whole company, skill testing_mode applies per-skill
        const chosenSet = selectedSetByGroup[group.id] || group.sets?.[0];
        if (!chosenSet) continue;
        for (const sk of chosenSet.skills || []) {
          if (sk.testing_mode === 'individual') {
            assignments.push({ skill_id: sk.id, candidate_ids: candidates.map((c) => c.id) });
          } else if (!addedTeamKeys.has(sk.id)) {
            assignments.push({ skill_id: sk.id, candidate_ids: null });
            addedTeamKeys.add(sk.id);
          }
        }
      }
    }

    if (assignments.length === 0) {
      alert('No skills to assign. Please select a path.');
      return;
    }
    setAssigningSkills(true);
    try {
      await api.post(
        `/api/test-sessions/${session.id}/assign-company-skills`,
        { assignments },
        { queueable: true }
      );
      await handleAdvanceStep('testing');
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to begin testing');
    } finally {
      setAssigningSkills(false);
    }
  };

  // When a SkillChecklistCard reports a result change (pass, fail, or null to clear),
  // update local skills state optimistically so the UI reflects it immediately.
  const handleSkillComplete = (assignmentId, result) => {
    setSkills((prev) =>
      prev.map((s) =>
        s.id === assignmentId ? { ...s, result: result ?? null, completed_at: result ? new Date().toISOString() : null } : s
      )
    );
  };

  // ─── Guards ───────────────────────────────────────────────────────────────────

  if (!station) {
    return (
      <div className="station-view">
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <h2>No Station Selected</h2>
          <button onClick={() => navigate('/proctor/select-station')}>Select Station</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="station-view"><div className="loading-spinner">Loading...</div></div>;
  }

  // ─── Queue view ───────────────────────────────────────────────────────────────

  if (view === 'queue') {
    const nextStep = getNextProctorStep();
    const isBriefing = stationState === 'briefing';

    return (
      <div className="station-view">
        <header className="proctor-header">
          <div className="header-left">
            <h1>#{station.station_number}</h1>
            <span className="station-name">{station.name}</span>
          </div>
          <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '...' : '🔄'}
          </button>
        </header>

        <section className="state-display-section">
          {stationState === 'waiting' && (
            <div className="state-card waiting">
              <h2>Waiting for Assignment</h2>
              <p>Command has not assigned a company to this station yet.</p>
            </div>
          )}

          {/* ── Briefing: show briefing text + company skill picker ── */}
          {isBriefing && session && (
            <div className="state-card briefing-card">
              <div className="state-badge">Briefing</div>
              <h2>{currentCompany?.name}</h2>
              {candidates.length > 0 && (
                <div className="queue-candidates">
                  {candidates.map((c) => (
                    <span key={c.id} className="candidate-chip">
                      {c.first_name} {c.last_name}
                    </span>
                  ))}
                </div>
              )}

              {station.briefing_text && (
                <div className="briefing-text-block">
                  <h3>Candidate Briefing</h3>
                  <p>{station.briefing_text}</p>
                </div>
              )}

              <div className="skill-selection-block">
                <h3>Select Path</h3>

                {(session.groups || []).length > 0 ? (
                  (session.groups || []).map((group) => (
                    <div key={group.id} className="skill-group-block">
                      {(session.groups || []).length > 1 && (
                        <div className="skill-group-header">
                          <span className="skill-group-name">{group.name}</span>
                        </div>
                      )}

                      {group.selection_mode === 'all' ? (
                        // All skills auto-selected — show Company/Individual breakdown directly
                        (() => {
                          const allGroupSkills = (group.sets || []).flatMap((s) => s.skills || []);
                          const companySkills = allGroupSkills.filter((sk) => sk.testing_mode !== 'individual');
                          const indivSkills = allGroupSkills.filter((sk) => sk.testing_mode === 'individual');
                          return (
                            <div className="set-confirmed">
                              {companySkills.length > 0 && (
                                <>
                                  <div className="skill-subgroup-label">🏢 Company Skills</div>
                                  {companySkills.map((sk) => (
                                    <div key={sk.id} className="skill-confirmed-row">
                                      <span>{sk.name}</span>
                                      <span className="skill-mode-badge team">Company</span>
                                    </div>
                                  ))}
                                </>
                              )}
                              {indivSkills.length > 0 && (
                                <>
                                  <div className="skill-subgroup-label">👤 Individual Skills</div>
                                  {indivSkills.map((sk) => (
                                    <div key={sk.id} className="skill-confirmed-row">
                                      <span>{sk.name}</span>
                                      <span className="skill-mode-badge individual">Individual</span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          );
                        })()
                      ) : (group.sets || []).length === 1 ? (
                        // Single path auto-selected — label it and show Company/Individual breakdown
                        (() => {
                          const set = group.sets[0];
                          const companySkills = (set.skills || []).filter((sk) => sk.testing_mode !== 'individual');
                          const indivSkills = (set.skills || []).filter((sk) => sk.testing_mode === 'individual');
                          return (
                            <div className="set-confirmed">
                              <div className="set-auto-label">{set.name || 'Primary Skills'} — auto-selected</div>
                              {companySkills.length > 0 && (
                                <>
                                  <div className="skill-subgroup-label">🏢 Company Skills</div>
                                  {companySkills.map((sk) => (
                                    <div key={sk.id} className="skill-confirmed-row">
                                      <span>{sk.name}</span>
                                      <span className="skill-mode-badge team">Company</span>
                                    </div>
                                  ))}
                                </>
                              )}
                              {indivSkills.length > 0 && (
                                <>
                                  <div className="skill-subgroup-label">👤 Individual Skills</div>
                                  {indivSkills.map((sk) => (
                                    <div key={sk.id} className="skill-confirmed-row">
                                      <span>{sk.name}</span>
                                      <span className="skill-mode-badge individual">Individual</span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          );
                        })()
                      ) : group.assignment_mode === 'individual' ? (
                        // Individual mode: one path per candidate
                        <div className="individual-path-selection">
                          {candidates.map((candidate) => (
                            <div key={candidate.id} className="candidate-path-row">
                              <div className="candidate-path-label">
                                {candidate.first_name} {candidate.last_name}
                              </div>
                              <div className="candidate-path-choices">
                                {(group.sets || []).map((set) => {
                                  const isSelected = candidatePathMap[group.id]?.[candidate.id]?.id === set.id;
                                  return (
                                    <button
                                      key={set.id}
                                      className={`set-choice-btn ${isSelected ? 'selected' : ''}`}
                                      onClick={() => selectSetForCandidate(set, group, candidate.id)}
                                    >
                                      <span className="set-choice-name">{set.name || 'Path'}</span>
                                      <span className="set-skills-preview">
                                        {(set.skills || []).map((sk) => sk.name).join(' · ')}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Company mode: proctor picks one path for the whole company
                        (group.sets || []).map((set) => {
                          const setSkillIds = (set.skills || []).map((sk) => sk.id);
                          const isSelected = setSkillIds.length > 0 && setSkillIds.every((id) => selectedSkillIds.includes(id));
                          return (
                            <button
                              key={set.id}
                              className={`set-choice-btn ${isSelected ? 'selected' : ''}`}
                              onClick={() => selectSet(set, group)}
                            >
                              <span className="set-choice-name">{set.name || 'Primary Skills'}</span>
                              <span className="set-skills-preview">
                                {(set.skills || []).map((sk) => sk.name).join(' · ')}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  ))
                ) : (
                  // Fallback: flat skill list from station
                  (station.skills || []).map((sk) => (
                    <label key={sk.skill_id} className="skill-checkbox-row">
                      <input
                        type="checkbox"
                        checked={selectedSkillIds.includes(sk.skill_id)}
                        onChange={() => toggleSkill(sk.skill_id)}
                      />
                      <span>{sk.skill_name}</span>
                    </label>
                  ))
                )}

                {/* ── Read-only skill summary — only for multi-path groups ── */}
                {/* Auto-resolved groups (selection_mode='all' or single set) already show   */}
                {/* the Company/Individual breakdown inline above — no duplication needed.   */}
                {candidates.length > 0 && (session.groups || []).some(
                  (g) => g.selection_mode !== 'all' && (g.sets || []).length > 1
                ) && (() => {
                  const teamSkills = [];
                  const individualSkills = [];
                  const perCandidateSummary = []; // individual-mode groups only

                  for (const group of session.groups || []) {
                    if (group.assignment_mode === 'individual') {
                      // Each candidate may be on a different path
                      for (const candidate of candidates) {
                        const chosenSet = candidatePathMap[group.id]?.[candidate.id];
                        if (chosenSet) {
                          perCandidateSummary.push({
                            candidate,
                            setName: chosenSet.name || 'Path',
                            teamInSet: (chosenSet.skills || []).filter((sk) => sk.testing_mode !== 'individual'),
                            indivInSet: (chosenSet.skills || []).filter((sk) => sk.testing_mode === 'individual'),
                          });
                        }
                      }
                    } else {
                      // Company-mode: get the selected set (or all sets if selection_mode='all')
                      const skills =
                        group.selection_mode === 'all'
                          ? (group.sets || []).flatMap((s) => s.skills || [])
                          : (selectedSetByGroup[group.id] || group.sets?.[0])?.skills || [];
                      for (const sk of skills) {
                        if (sk.testing_mode === 'individual') {
                          if (!individualSkills.find((s) => s.id === sk.id)) individualSkills.push(sk);
                        } else {
                          if (!teamSkills.find((s) => s.id === sk.id)) teamSkills.push(sk);
                        }
                      }
                    }
                  }

                  if (teamSkills.length === 0 && individualSkills.length === 0 && perCandidateSummary.length === 0) return null;

                  return (
                    <div className="skill-assignment-panel">
                      <h4>Skill Summary</h4>

                      {/* Company-wide team skills */}
                      {teamSkills.length > 0 && (
                        <div className="assignment-group">
                          <div className="assignment-group-label">🏢 Company Skills — evaluated once for the whole company</div>
                          {teamSkills.map((sk) => (
                            <div key={sk.id} className="skill-confirmed-row">
                              <span>{sk.name}</span>
                              <span className="skill-mode-badge team">Company</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Individual skills — all candidates */}
                      {individualSkills.length > 0 && (
                        <div className="assignment-group">
                          <div className="assignment-group-label">👤 Individual Skills — tested by each candidate</div>
                          {individualSkills.map((sk) => (
                            <div key={sk.id} className="skill-confirmed-row">
                              <span>{sk.name}</span>
                              <span className="skill-mode-badge individual">Individual</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Per-candidate path summaries (individual-mode groups, e.g. Ropes) */}
                      {perCandidateSummary.map(({ candidate, setName, teamInSet, indivInSet }) => (
                        <div key={candidate.id} className="assignment-group">
                          <div className="assignment-group-label">
                            👤 {candidate.first_name} {candidate.last_name} — {setName}
                          </div>
                          {teamInSet.map((sk) => (
                            <div key={sk.id} className="skill-confirmed-row">
                              <span>{sk.name}</span>
                              <span className="skill-mode-badge team">Company</span>
                            </div>
                          ))}
                          {indivInSet.map((sk) => (
                            <div key={sk.id} className="skill-confirmed-row">
                              <span>{sk.name}</span>
                              <span className="skill-mode-badge individual">Individual</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="session-actions">
                <button
                  className="btn-primary large"
                  onClick={handleBeginTesting}
                  disabled={assigningSkills || (() => {
                    // Validate all groups are ready
                    if (!session?.groups?.length) return true;
                    for (const group of session.groups || []) {
                      if (group.selection_mode === 'all') continue;
                      if (group.assignment_mode === 'individual') {
                        if (!candidates.every((c) => candidatePathMap[group.id]?.[c.id])) return true;
                      } else {
                        if ((group.sets || []).length > 1 && !selectedSetByGroup[group.id]) return true;
                      }
                    }
                    return false;
                  })()}
                >
                  {assigningSkills ? 'Starting...' : '▶ Begin Testing'}
                </button>
              </div>
            </div>
          )}

          {/* ── Normal workflow steps (non-briefing, non-waiting, non-testing) ── */}
          {!isBriefing && stationState !== 'waiting' && stationState !== 'testing' && currentCompany && (
            <div className={`state-card ${stationState}`}>
              <div className="state-badge">
                {workflowSteps.find((s) => s.key === stationState)?.label ||
                  stationState.charAt(0).toUpperCase() + stationState.slice(1).replace('_', ' ')}
              </div>
              <h2>{currentCompany.name}</h2>
              {candidates.length > 0 && (
                <div className="queue-candidates">
                  {candidates.map((c) => (
                    <span key={c.id} className="candidate-chip">
                      {c.first_name} {c.last_name}
                    </span>
                  ))}
                </div>
              )}
              <div className="session-actions">
                {/* Inline confirm for Release Company */}
                {confirmAction === 'complete' && (
                  <div className="inline-confirm">
                    <p>Release this company from the station?</p>
                    <div className="inline-confirm-actions">
                      <button onClick={handleCancelAction} style={{ background: 'var(--gray-200)', color: 'var(--gray-700)' }}>Cancel</button>
                      <button onClick={handleConfirmAction} style={{ background: 'var(--success)', color: 'white' }}>✓ Release Company</button>
                    </div>
                  </div>
                )}
                {!confirmAction && nextStep && (
                  <button className="btn-primary large" onClick={() => handleAdvanceStep(nextStep.key)}>
                    ✓ {nextStep.label}
                  </button>
                )}
                {/* End of workflow: all steps done, company still at station */}
                {!confirmAction && !nextStep && isPostTestingState && session && (
                  <button className="btn-success large" onClick={() => setConfirmAction('complete')}>
                    ✓ Release Company
                  </button>
                )}
              </div>
            </div>
          )}

          {stationState === 'testing' && currentCompany && (
            <div className="state-card testing">
              <div className="state-badge active">Testing</div>
              <h2>{currentCompany.name}</h2>
              {candidates.length > 0 && (
                <div className="queue-candidates">
                  {candidates.map((c) => (
                    <span key={c.id} className="candidate-chip">
                      {c.first_name} {c.last_name}
                    </span>
                  ))}
                </div>
              )}
              <p>Testing is in progress.</p>
              <button className="btn-primary large" onClick={() => navigate('/proctor/testing')}>
                Go to Testing View ▶
              </button>
            </div>
          )}
        </section>
      </div>
    );
  }

  // ─── Testing view ─────────────────────────────────────────────────────────────

  if (view === 'testing') {
    if (!session) {
      return (
        <div className="station-view">
          <header className="proctor-header">
            <div className="header-left">
              <h1>#{station.station_number}</h1>
              <span className="station-name">{station.name}</span>
            </div>
          </header>
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h2>No Active Session</h2>
            <p>Wait for a company to be dispatched to this station.</p>
          </div>
        </div>
      );
    }

    const pendingSkills = skills.filter((s) => !s.result);
    const completedSkills = skills.filter((s) => s.result);
    // Split assignments by type — individual skills have a candidate_id, team skills do not
    const individualAssignments = skills.filter((s) => s.candidate_id != null);
    const teamAssignments = skills.filter((s) => s.candidate_id == null);

    return (
      <div className="station-view">
        <header className="proctor-header">
          <div className="header-left">
            <h1>#{station.station_number}</h1>
            <span className="session-company-name">{session.company_name}</span>
          </div>
          <div className="header-right">
            <div className="session-timer">{formatTime(sessionTimer)}</div>
            <button className="btn-secondary small" onClick={() => setCollapseAll((n) => n + 1)}>
              Minimize All
            </button>
            <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? '…' : '🔄'}
            </button>
          </div>
        </header>

        <section className="testing-section">
          {skills.length === 0 ? (
            <div className="empty-state">
              <p>No skills assigned. Return to Queue to begin a briefing.</p>
              <button className="btn-secondary" onClick={() => navigate('/proctor/queue')}>
                ← Back to Queue
              </button>
            </div>
          ) : (
            <>
              {/* Company/team skills FIRST — matches template skill order */}
              {teamAssignments.length > 0 && (
                <div className="candidate-section">
                  <div className="candidate-section-header">
                    <span className="candidate-name">🏢 Company Skills</span>
                  </div>
                  {teamAssignments.map((assignment) => (
                    <SkillChecklistCard
                      key={assignment.id}
                      assignment={assignment}
                      candidateName={null}
                      sessionId={session.id}
                      timer={sessionTimer}
                      onComplete={handleSkillComplete}
                      collapseAll={collapseAll}
                    />
                  ))}
                </div>
              )}

              {/* Individual skills grouped by candidate */}
              {candidates.map((candidate) => {
                const candidateSkills = individualAssignments.filter((s) => s.candidate_id === candidate.id);
                if (candidateSkills.length === 0) return null;
                return (
                  <div key={candidate.id} className="candidate-section">
                    <div className="candidate-section-header">
                      <span className="candidate-name">
                        {candidate.first_name} {candidate.last_name}
                      </span>
                      <span className="candidate-id">{candidate.candidate_id}</span>
                    </div>
                    {candidateSkills.map((assignment) => (
                      <SkillChecklistCard
                        key={assignment.id}
                        assignment={assignment}
                        candidateName={null}
                        sessionId={session.id}
                        timer={sessionTimer}
                        onComplete={handleSkillComplete}
                        collapseAll={collapseAll}
                      />
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </section>

        {skills.length > 0 && (
          <div className="session-actions-bar">
            {confirmAction && (
              <div className="inline-confirm">
                <p>
                  {confirmAction === 'complete'
                    ? (() => {
                        const post = getFirstPostTestingStep();
                        return post
                          ? `Mark testing complete and proceed to "${post.label}"?`
                          : 'Complete this session and release the company?';
                      })()
                    : 'Return this company to staging without completing?'}
                </p>
                <div className="inline-confirm-actions">
                  <button
                    onClick={handleCancelAction}
                    style={{ background: 'var(--gray-200)', color: 'var(--gray-700)' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmAction}
                    style={{ background: confirmAction === 'complete' ? 'var(--success)' : 'var(--warning)', color: 'white' }}
                  >
                    {confirmAction === 'complete'
                      ? (() => {
                          const post = getFirstPostTestingStep();
                          return post ? `Go to ${post.label} →` : 'Complete Session';
                        })()
                      : 'Return to Staging'}
                  </button>
                </div>
              </div>
            )}
            {!confirmAction && (
              <>
                <div className="session-progress-summary">
                  <span className="summary-done">✓ {completedSkills.length} done</span>
                  <span className="summary-pending">{pendingSkills.length} pending</span>
                </div>
                <div className="session-actions-bar-buttons">
                  <button className="btn-secondary" onClick={handleReturnToStaging}>
                    ↩ Return
                  </button>
                  <button
                    className="btn-success"
                    onClick={handleCompleteSession}
                    disabled={pendingSkills.length > 0}
                  >
                    {pendingSkills.length > 0
                      ? `✓ Testing Complete (${pendingSkills.length} left)`
                      : (() => {
                          const post = getFirstPostTestingStep();
                          return post ? `✓ Testing Complete → ${post.label}` : '✓ Complete Session';
                        })()}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Results view ─────────────────────────────────────────────────────────────

  if (view === 'results') {
    const allResults = stationResults.flatMap((s) => s.skill_results);
    const totalPass = allResults.filter((r) => r.result === 'pass').length;
    const totalFail = allResults.filter((r) => r.result === 'fail').length;

    return (
      <div className="station-view">
        <header className="proctor-header">
          <div className="header-left">
            <h1>#{station.station_number}</h1>
            <span className="station-name">Results</span>
          </div>
          <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '...' : '🔄'}
          </button>
        </header>

        {allResults.length > 0 ? (
          <>
            <div className="results-summary">
              <div className="summary-card pass">
                <div className="summary-number">{totalPass}</div>
                <div className="summary-label">Passed</div>
              </div>
              <div className="summary-card fail">
                <div className="summary-number">{totalFail}</div>
                <div className="summary-label">Failed</div>
              </div>
              <div className="summary-card total">
                <div className="summary-number">{allResults.length}</div>
                <div className="summary-label">Total</div>
              </div>
            </div>

            {stationResults.filter((s) => s.skill_results.length > 0).map((s) => (
              <section key={s.id} className="results-section">
                <h2>
                  {s.company_name}
                  {s.status === 'active' && <span className="session-badge active"> — In Progress</span>}
                </h2>
                <div className="results-list">
                  {s.skill_results.map((r) => (
                    <div key={r.id} className={`result-item ${r.result}`}>
                      <div className="result-icon">{r.result === 'pass' ? '✅' : '❌'}</div>
                      <div className="result-info">
                        <div className="result-skill">{r.skill_name}</div>
                        {(r.first_name || r.last_name) && (
                          <div className="result-candidate">
                            {r.first_name} {r.last_name}
                          </div>
                        )}
                      </div>
                      <div className="result-time">
                        {r.completed_at ? new Date(r.completed_at).toLocaleTimeString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h2>No Results</h2>
            <p>Results will appear here during testing.</p>
          </div>
        )}
      </div>
    );
  }

  return null;
}
